import Button from '@/components/Button.jsx';
import DocsCard from '@/components/DocsCard.jsx';
import Input from '@/components/Input.jsx';
import { FiCheck, FiGlobe, IoSend, MdOutlineNotes } from '@/components/react-icons/main';
import Skeleton from '@/components/Skeleton.jsx';
import confirmDialog from '@/lib/confirmDialog.js';
import { getAppDocContent, putAppDocContent } from '@/redux/actions/gravityActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { appDocByTypeAndObject } from '@/redux/selectors/aiSelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DocStatus from './DocStatus';

const DEBOUNCE_DELAY = 1000;

const AppDocContent = ({ appId, type, objectId, docUrl, version = 'summary', className = '' }) => {
  const [content, setContent] = useState('');
  const [processedHtml, setProcessedHtml] = useState('');
  const [message, setMessage] = useState('');
  const [userCards, setUserCards] = useState([]);
  const [editingCardId, setEditingCardId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const previousDocKeyRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const docsContentRef = useRef(null);
  const textareaRef = useRef(null);

  const isLoading = useArgSelector(selectors.isLoading, 'getAppDocContent');
  const selectedDoc = useSelector((state) => appDocByTypeAndObject(state, type, objectId));
  const loaded = !!selectedDoc?.meta?.loaded;
  const processedFinishedOn = selectedDoc?.meta?.processed?.finishedOn;
  const hasContentToShow = loaded || (isInitialized && (content?.html || content?.userContext?.length > 0));

  const saveUserCards = useCallback(
    async (cards) => {
      if (docUrl && (!objectId || !appId || !type || !isInitialized)) return;

      try {
        setIsSaving(true);
        await putAppDocContent({ appId, type, objectId, userContext: cards, version });
      } catch (error) {
        console.error('Failed to save user cards:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [objectId, appId, type, isInitialized, docUrl, version]
  );

  const debouncedSaveUserCards = useCallback(
    (cards) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        saveUserCards(cards);
      }, DEBOUNCE_DELAY);
    },
    [saveUserCards]
  );

  const scrollToBottom = () => {
    if (docsContentRef.current) {
      docsContentRef.current.scrollTop = docsContentRef.current.scrollHeight;
    }
  };

  const handleSend = () => {
    if (!message.trim()) return;

    const newCards = editingCardId !== null ? userCards.map((card) => (card.id === editingCardId ? { ...card, content: message } : card)) : [...userCards, { id: Date.now(), title: 'User Context', content: message }];

    setUserCards(newCards);
    setEditingCardId(null);
    setMessage('');
    debouncedSaveUserCards(newCards);

    // Scroll to bottom after a short delay to ensure the new card is rendered
    setTimeout(scrollToBottom, 100);
    setTimeout(() => {
      textareaRef?.current?.focus?.();
    }, 0);
  };

  const handleEdit = (cardId) => {
    const card = userCards.find((c) => c.id === cardId);
    if (card) {
      setMessage(card.content);
      setEditingCardId(cardId);
    }
  };

  const handleDelete = async (cardId) => {
    const confirmed = await confirmDialog({
      title: 'Delete Context',
      message: 'Are you sure you want to delete this context? This action cannot be undone.',
      confirm: 'Delete'
    });

    if (confirmed) {
      const newCards = userCards.filter((card) => card.id !== cardId);
      setUserCards(newCards);
      if (editingCardId === cardId) {
        setEditingCardId(null);
        setMessage('');
      }
      debouncedSaveUserCards(newCards);
    }
  };

  const handleTextareaKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Load initial content
  useEffect(() => {
    if (objectId) {
      setIsInitialized(false); // Reset initialization flag
      getAppDocContent({ appId, type, objectId, version }, (data) => {
        if (data === undefined) {
          return;
        }
        setContent(data);
        setIsInitialized(true);
      });
    }
  }, [appId, type, objectId, version]);

  // Handle doc target changes
  useEffect(() => {
    const currentDocKey = `${type || ''}:${objectId || ''}`;

    if (previousDocKeyRef.current && previousDocKeyRef.current !== currentDocKey) {
      // Clear state when switching between different objects
      setUserCards([]);
      setMessage('');
      setEditingCardId(null);
      setIsInitialized(false);
    }
    previousDocKeyRef.current = currentDocKey;
  }, [type, objectId]);

  // Reload content when doc status changes
  useEffect(() => {
    if (processedFinishedOn && objectId) {
      getAppDocContent({ appId, type, objectId, version }, (data) => {
        if (data === undefined) {
          console.log(`AppDocContent was opened using an invalid objectId: ${objectId}`);
          return;
        }
        setContent(data);
        setIsInitialized(true);
      });
    }
  }, [processedFinishedOn, appId, type, objectId, version]);

  useEffect(() => {
    if (!content?.html) return;

    const processHtml = (htmlString) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      return doc.body.innerHTML;
    };

    const processed = processHtml(content.html);
    setProcessedHtml(processed);
  }, [content?.html]);

  // Update userCards from content
  useEffect(() => {
    if (content?.userContext && Array.isArray(content.userContext)) {
      setUserCards(content.userContext);
    }
  }, [content?.userContext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`app-doc-content ${className}`}>
      <div className="docs-content-wrapper" ref={docsContentRef}>
        {!isLoading && (
          <DocsCard title="Extracted Documentation" icon={FiGlobe} className="mb-4" content={hasContentToShow ? processedHtml : null}>
            {!hasContentToShow && <DocStatus type={type} objectId={objectId} />}
          </DocsCard>
        )}
        {userCards.map((card) => (
          <DocsCard icon={MdOutlineNotes} key={card.id} title={card.title} content={card.content} onEdit={() => handleEdit(card.id)} onDelete={() => handleDelete(card.id)} isEditing={editingCardId === card.id} isUserCard className="mb-4" />
        ))}
        {isLoading && (
          <div className="loading-content">
            <Skeleton count={25} skeletonStyle={{ height: 24, borderRadius: 4 }} />
          </div>
        )}
      </div>
      <div className="docs-footer">
        <div className="input-wrapper">
          <Input type="textarea" value={message} ref={textareaRef} onKeyUp={handleTextareaKeyPress} onChange={(e) => setMessage(e.target.value)} placeholder={editingCardId || !docUrl ? 'Edit your context...' : !hasContentToShow ? 'Wait for documentation to load...' : 'Add context about this action...'} autoSize rows={2} disabled={docUrl ? !isInitialized || isSaving || !hasContentToShow : false} />
          <Button className={`action-button ${message?.trim() ? 'primary' : 'ghost'}`} onClick={handleSend} disabled={docUrl ? !message.trim() || !isInitialized || isSaving || !hasContentToShow : false} title={editingCardId ? 'Update' : 'Add Context'} icon={editingCardId ? FiCheck : IoSend} />
        </div>
      </div>
    </div>
  );
};

export default AppDocContent;
